import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '../../lib/api';
import { Session } from '../../types/session';
import { Button, Card } from '../../components/ui';
import { useWebSocket } from '../../providers/WebSocketProvider';

interface SessionWithAttendance extends Session {
  attendanceCount?: number;
}

interface CheckIn {
  id: string;
  attendeeId: string;
  attendeeName: string;
  attendeeEmail: string;
  checkedInAt: string;
}

export default function AttendanceTracker() {
  const queryClient = useQueryClient();
  const { isConnected } = useWebSocket();
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [showCheckInList, setShowCheckInList] = useState(false);

  // Auto-refresh data when component mounts (when navigating to this page)
  useEffect(() => {
    console.log('🔄 AttendanceTracker mounted - refreshing data');
    queryClient.invalidateQueries({ queryKey: ['admin', 'sessions'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'checkins'] });
  }, []); // Empty dependency array = runs only on mount

  // Fetch all sessions with attendance data
  const { data: sessions, isLoading: sessionsLoading } = useQuery<SessionWithAttendance[]>({
    queryKey: ['admin', 'sessions'],
    queryFn: async () => {
      const sessions = await fetchApi<SessionWithAttendance[]>('/admin/sessions');
      // Fetch attendance count for each session
      const sessionsWithAttendance = await Promise.all(
        sessions.map(async (session) => {
          try {
            const checkIns = await fetchApi<CheckIn[]>(`/admin/checkins/session/${session.id}`);
            return {
              ...session,
              attendanceCount: checkIns.length,
            };
          } catch (error) {
            return {
              ...session,
              attendanceCount: 0,
            };
          }
        })
      );
      return sessionsWithAttendance;
    },
    refetchInterval: 30000, // Refetch every 30 seconds as backup
  });

  // Fetch check-ins for selected session
  const { data: checkIns, isLoading: checkInsLoading } = useQuery<CheckIn[]>({
    queryKey: ['checkins', selectedSessionId],
    queryFn: () => fetchApi<CheckIn[]>(`/admin/checkins/session/${selectedSessionId}`),
    enabled: !!selectedSessionId && showCheckInList,
  });

  // Listen for WebSocket attendance updates
  useEffect(() => {
    // Invalidate queries when attendance updates are received
    // The WebSocket provider already handles attendance:updated events
    // We just need to ensure our queries are invalidated when data changes
    const intervalId = setInterval(() => {
      // Periodic refresh as backup to WebSocket updates
      queryClient.invalidateQueries({ queryKey: ['admin', 'sessions'] });
    }, 60000); // Every 60 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [queryClient, selectedSessionId]);

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getCapacityStatus = (attendance: number, capacity: number) => {
    const percentage = (attendance / capacity) * 100;
    if (percentage >= 100) {
      return { color: 'text-red-600', bg: 'bg-red-50', label: 'At Capacity' };
    } else if (percentage >= 80) {
      return { color: 'text-orange-600', bg: 'bg-orange-50', label: 'Nearly Full' };
    } else if (percentage >= 50) {
      return { color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Filling Up' };
    }
    return { color: 'text-green-600', bg: 'bg-green-50', label: 'Available' };
  };

  const selectedSession = sessions?.find((s) => s.id === selectedSessionId);

  if (sessionsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-usc-garnet mx-auto"></div>
          <p className="mt-4 text-usc-gray-600">Loading attendance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-usc-garnet">Attendance Tracking</h2>
            <p className="text-usc-gray-600 mt-2">Monitor real-time attendance for all sessions</p>
          </div>
          <div className="flex items-center space-x-2">
            <div
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
                isConnected ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}
            >
              <div
                className={`w-2 h-2 rounded-full ${
                  isConnected ? 'bg-green-500' : 'bg-red-500'
                }`}
              ></div>
              <span className="text-sm font-medium">
                {isConnected ? 'Live Updates' : 'Disconnected'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Sessions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {sessions && sessions.length > 0 ? (
          sessions.map((session) => {
            const attendance = session.attendanceCount || 0;
            const capacity = session.room.capacity;
            const status = getCapacityStatus(attendance, capacity);

            return (
              <Card
                key={session.id}
                className={`p-4 cursor-pointer transition-all hover:shadow-lg ${
                  selectedSessionId === session.id ? 'ring-2 ring-usc-garnet' : ''
                }`}
                onClick={() => {
                  setSelectedSessionId(session.id);
                  setShowCheckInList(true);
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-bold text-usc-garnet line-clamp-2 flex-1">
                    {session.title}
                  </h3>
                </div>

                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-usc-gray-600">Time:</span>{' '}
                    <span className="text-usc-gray-900">
                      {formatTime(session.startTime)} - {formatTime(session.endTime)}
                    </span>
                  </div>
                  <div>
                    <span className="text-usc-gray-600">Room:</span>{' '}
                    <span className="text-usc-gray-900">
                      {session.room.name} ({session.room.building})
                    </span>
                  </div>
                </div>

                {/* Attendance Display */}
                <div className="mt-4 pt-4 border-t border-usc-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-usc-garnet">
                        {attendance}
                        <span className="text-lg text-usc-gray-500">/{capacity}</span>
                      </div>
                      <div className="text-xs text-usc-gray-600 mt-1">Attendees</div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                      {status.label}
                    </div>
                  </div>

                  {/* Capacity Bar */}
                  <div className="mt-3">
                    <div className="w-full bg-usc-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          attendance >= capacity
                            ? 'bg-red-500'
                            : attendance >= capacity * 0.8
                            ? 'bg-orange-500'
                            : 'bg-green-500'
                        }`}
                        style={{
                          width: `${Math.min((attendance / capacity) * 100, 100)}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })
        ) : (
          <Card className="col-span-full p-12 text-center">
            <p className="text-usc-gray-600 text-lg">No sessions found</p>
            <p className="text-usc-gray-500 mt-2">Create sessions to start tracking attendance</p>
          </Card>
        )}
      </div>

      {/* Check-In List Modal */}
      {showCheckInList && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-usc-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-2xl font-bold text-usc-garnet mb-2">
                    {selectedSession.title}
                  </h3>
                  <div className="flex items-center space-x-4 text-sm text-usc-gray-600">
                    <span>
                      {formatTime(selectedSession.startTime)} - {formatTime(selectedSession.endTime)}
                    </span>
                    <span>•</span>
                    <span>
                      {selectedSession.room.name} ({selectedSession.room.building})
                    </span>
                  </div>
                  <div className="mt-3 flex items-center space-x-4">
                    <div className="text-2xl font-bold text-usc-garnet">
                      {selectedSession.attendanceCount || 0}
                      <span className="text-base text-usc-gray-500">
                        /{selectedSession.room.capacity}
                      </span>
                      <span className="text-sm text-usc-gray-600 font-normal ml-2">
                        attendees
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowCheckInList(false);
                    setSelectedSessionId('');
                  }}
                >
                  Close
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <h4 className="text-lg font-bold text-usc-gray-900 mb-4">Check-In List</h4>

              {checkInsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-usc-garnet mx-auto"></div>
                    <p className="mt-3 text-usc-gray-600">Loading check-ins...</p>
                  </div>
                </div>
              ) : checkIns && checkIns.length > 0 ? (
                <div className="space-y-2">
                  {checkIns.map((checkIn, index) => (
                    <div
                      key={checkIn.id}
                      className="flex items-center justify-between p-3 bg-usc-gray-50 rounded-lg hover:bg-usc-gray-100 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 bg-usc-garnet text-white rounded-full text-sm font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium text-usc-gray-900">
                            {checkIn.attendeeName}
                          </div>
                          <div className="text-sm text-usc-gray-600">
                            {checkIn.attendeeEmail}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-usc-gray-600">
                        {formatDateTime(checkIn.checkedInAt)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-usc-gray-600">No check-ins yet</p>
                  <p className="text-sm text-usc-gray-500 mt-1">
                    Check-ins will appear here as attendees scan their QR codes
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
